import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Button, Stack, Chip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import RichEditor from "../../../components/rich-editor";
import { dialogCloseSx, primaryButtonSx, countChipSx } from "../../CurriculumManagementAdmin/components/buttonStyles";
import { isEmptyHtml } from "../../CurriculumManagementAdmin/utils/html";
import { BadgeChip } from "./AttendanceMarkers";

/**
 * A single session opened from the calendar: what it covers, and whether this agent was there.
 * Mirrors the admin's TrainingDetailsDialog minus the attendance roster — an agent sees only their
 * own attendance and the badge it earned them.
 */
const TrainingDetailsDialog = ({ open, onClose, training }) => {
	if (!training) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Elementor sizes the paper to its content and ignores MUI's maxWidth, so the sm
						// breakpoint is pinned here. Width stays margin-aware so it never overflows.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "600px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							{training.title}
						</Typography>
						<Typography variant="body2" color="text.secondary" noWrap>
							{training.curriculumTitle}
						</Typography>

						<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
							<Chip
								size="small"
								icon={<EventOutlinedIcon />}
								label={dayjs(training.date).format("ddd, MMM D, YYYY")}
								sx={{ ...countChipSx, cursor: "default", "& .MuiChip-icon": { color: "#1A3F7A !important", fontSize: 15 } }}
							/>
						</Stack>
					</Box>

					<IconButton onClick={onClose} size="small" aria-label="Close training details" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ width: "100%" }}>
				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Description
				</Typography>

				<Box sx={{ mt: 0.5, mb: 2 }}>
					{isEmptyHtml(training.description) ? (
						<Typography variant="body2" color="text.secondary">
							No description provided.
						</Typography>
					) : (
						<RichEditor value={training.description} readOnly />
					)}
				</Box>

				<Divider sx={{ mb: 2 }} />

				<Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary", textTransform: "uppercase" }}>
					Your Attendance
				</Typography>

				{/* The green panel is the confirmation; the badge beside it is what the attendance earned. */}
				<Box
					sx={{
						mt: 1,
						p: 2,
						borderRadius: 2,
						border: "1px solid",
						borderColor: training.attended ? "#B7EBD5" : "#DDE5EE",
						backgroundColor: training.attended ? "#F6FFFB" : "#FAFCFE",
					}}
				>
					{training.attended ? (
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
								<CheckCircleIcon sx={{ color: "#0F7A53", fontSize: 22, flexShrink: 0 }} />
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 700, color: "#0F7A53" }}>
										You attended this training
									</Typography>
									{training.attended_at && (
										<Typography variant="caption" color="text.secondary">
											Marked on {dayjs(training.attended_at).format("MMMM D, YYYY")}
										</Typography>
									)}
								</Box>
							</Stack>

							<Box sx={{ flexShrink: 0 }}>
								<Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary", fontWeight: 700 }}>
									Badge earned
								</Typography>
								<BadgeChip training={training} maxWidth="none" />
							</Box>
						</Stack>
					) : (
						<Typography variant="body2" color="text.secondary">
							You have not been marked as attending this training yet.
						</Typography>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} variant="contained" sx={primaryButtonSx}>
					Done
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default TrainingDetailsDialog;
